// File: apps/web/src/features/import/WorkItemSelector.tsx
// Version: 2026.4.7 | Sprint 7 Wave 3 cleanup — real /api/v1/work-items route
//
// CHANGE LOG (cleanup pass):
//   • Fixed the route from the message-2 guess (/api/v1/workitems/search)
//     to the real one (/api/v1/work-items, with hyphen, no /search suffix).
//     Verified via forgetrack-code-intel against WorkItemsController.cs.
//   • Removed the made-up `projectId` field from WorkItemRow — the real
//     WorkItemResponse DTO doesn't have it. Group by ProjectName instead.
//   • Removed the heuristicSuggestedId prop — heuristic match is at the
//     PROJECT level, not the work item level, so it never made sense here.
//
// PURPOSE
//
// Lets the user pick a work item to map a staged conversation to. The
// AI-suggested work item (if present) is shown at the top with a violet
// "AI" badge. The rest of the list is searchable client-side.
//
// DATA SOURCE
//
// We deliberately fetch the entire work-items list once on mount rather
// than typing-debounced server search. Most tenants have <500 work items
// and the full list fits in a single response. If that ever stops being
// true, swap to a debounced /api/v1/work-items?search=… query.

import { useEffect, useMemo, useState, useCallback } from 'react';
import { useApiClient } from '@forgetrack/api-client';
import { ENGINE_COLORS as C, ENGINE_FONTS as F } from '../../components/engine/tokens';

// Mirrors api/DTOs/WorkItemDtos.cs WorkItemResponse — only the fields we use.
// SearchAsync returns List<WorkItemResponse> (a flat array, not paged).
interface WorkItemRow {
  id:           number;
  title:        string;
  description:  string | null;
  status:       string;
  phaseId:      number;
  phaseName:    string | null;
  projectName:  string | null;
}

interface WorkItemSelectorProps {
  value:              number | null;
  onChange:           (workItemId: number | null) => void;
  aiSuggestedId?:     number | null;
  filterProjectId?:   number | null;
  filterProjectName?: string | null;
}

export function WorkItemSelector({
  value,
  onChange,
  aiSuggestedId,
  filterProjectId,
  filterProjectName,
}: WorkItemSelectorProps) {
  const { createApi } = useApiClient();
  const [items,   setItems]   = useState<WorkItemRow[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState<string | null>(null);
  const [query,   setQuery]   = useState('');
  const [newTitle,    setNewTitle]    = useState('');
  const [creating,    setCreating]    = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  // Fetch all work items. Extracted as a callback so we can re-invoke
  // after inline creation without remounting.
  const fetchItems = useCallback(async () => {
    try {
      setLoading(true);
      const api = await createApi();
      const response = await api.get<WorkItemRow[] | { items: WorkItemRow[] }>(
        '/api/v1/work-items?page=1&pageSize=200&sortBy=Title&sortDirection=asc'
      );
      const list = Array.isArray(response.data)
        ? response.data
        : response.data.items ?? [];
      setItems(list);
    } catch (err: any) {
      setError(err?.response?.data?.error ?? err?.message ?? 'Failed to load work items');
    } finally {
      setLoading(false);
    }
  }, [createApi]);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  // Inline "quick create" — auto-creates a default phase if needed,
  // then creates the work item and selects it.
  const handleQuickCreate = useCallback(async () => {
    if (!filterProjectId || !newTitle.trim()) return;
    setCreating(true);
    setCreateError(null);
    try {
      const api = await createApi();

      // 1. Fetch phases for the selected project
      const phasesRes = await api.get<{ id: number; name: string }[]>(
        `/api/v1/phases/by-project/${filterProjectId}`
      );
      let phaseId: number;
      const phases = Array.isArray(phasesRes.data) ? phasesRes.data : [];

      if (phases.length > 0) {
        phaseId = phases[0].id;
      } else {
        // 2. No phases — create a default one
        const newPhase = await api.post<{ id: number }>('/api/v1/phases', {
          projectId: filterProjectId,
          name: 'General',
        });
        phaseId = newPhase.data.id;
      }

      // 3. Create the work item
      const newWi = await api.post<{ id: number }>('/api/v1/work-items', {
        phaseId,
        title: newTitle.trim(),
      });

      // 4. Refresh list and select the new item
      await fetchItems();
      onChange(newWi.data.id);
      setNewTitle('');
    } catch (err: any) {
      setCreateError(err?.response?.data?.message ?? err?.message ?? 'Failed to create');
    } finally {
      setCreating(false);
    }
  }, [createApi, filterProjectId, newTitle, fetchItems, onChange]);

  // Filter by project name (from parent dropdown), then by search query,
  // then pin the AI-suggested row to the top.
  const ordered = useMemo(() => {
    if (!items) return [];

    // Step 1: Project filter
    const projectFiltered = filterProjectName
      ? items.filter(it =>
          it.projectName?.toLowerCase() === filterProjectName.toLowerCase())
      : items;

    // Step 2: Search query filter
    const q = query.trim().toLowerCase();
    const matches = q
      ? projectFiltered.filter(it =>
          it.title.toLowerCase().includes(q)
          || it.projectName?.toLowerCase().includes(q)
          || it.description?.toLowerCase().includes(q)
          || it.phaseName?.toLowerCase().includes(q))
      : projectFiltered;

    // Step 3: Pin AI suggestion to top
    const aiRow = aiSuggestedId ? items.find(it => it.id === aiSuggestedId) : null;
    if (!aiRow) return matches;

    const rest = matches.filter(m => m.id !== aiRow.id);
    return [aiRow, ...rest];
  }, [items, query, aiSuggestedId, filterProjectName]);

  if (loading) {
    return (
      <div style={loadingBoxStyle}>
        Loading work items…
      </div>
    );
  }

  if (error || !items) {
    return (
      <div style={{ ...loadingBoxStyle, color: C.coral, borderColor: `${C.coral}38` }}>
        {error ?? 'No work items available'}
      </div>
    );
  }

  return (
    <div>
      <input
        type="text"
        value={query}
        onChange={e => setQuery(e.target.value)}
        placeholder="Search work items by title, project, or description…"
        style={searchInputStyle}
      />

      <div style={{
        marginTop:    8,
        maxHeight:    320,
        overflowY:    'auto',
        border:       `1px solid ${C.faint}`,
        borderRadius: 8,
        background:   C.surface,
      }}>
        {ordered.length === 0 && (
          <div style={emptyRowStyle}>
            {items.length === 0 && !filterProjectId
              ? 'No work items in this tenant yet — select a project above to create one.'
              : items.length === 0
                ? 'No work items yet — create one below.'
                : `No work items match "${query}"`}
          </div>
        )}

        {/* Inline creation when list is empty and a project is selected */}
        {items.length === 0 && filterProjectId && (
          <div style={{ padding: '12px 14px', borderTop: `1px solid ${C.faint}` }}>
            <div style={{
              fontSize: 10, letterSpacing: '0.1em', textTransform: 'uppercase',
              color: C.cyan, marginBottom: 8, fontFamily: F.display, fontWeight: 600,
            }}>
              QUICK CREATE WORK ITEM
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <input
                type="text"
                value={newTitle}
                onChange={e => setNewTitle(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleQuickCreate()}
                placeholder="Work item title…"
                disabled={creating}
                style={{
                  ...searchInputStyle,
                  flex: 1,
                  fontSize: 13,
                  padding: '8px 12px',
                }}
              />
              <button
                onClick={handleQuickCreate}
                disabled={creating || !newTitle.trim()}
                style={{
                  padding: '8px 16px', fontSize: 12, fontWeight: 600,
                  fontFamily: F.display, borderRadius: 8,
                  background: creating || !newTitle.trim() ? C.faint : C.neon,
                  color: creating || !newTitle.trim() ? C.dim : '#050910',
                  border: 'none',
                  cursor: creating || !newTitle.trim() ? 'not-allowed' : 'pointer',
                  boxShadow: creating || !newTitle.trim() ? 'none' : `0 0 12px ${C.neonGlow}`,
                  flexShrink: 0,
                }}
              >
                {creating ? 'Creating…' : 'Create'}
              </button>
            </div>
            {createError && (
              <div style={{ marginTop: 6, fontSize: 12, color: C.coral, fontFamily: F.display }}>
                {createError}
              </div>
            )}
          </div>
        )}

        {ordered.map(item => {
          const isSelected = value === item.id;
          const isAi       = item.id === aiSuggestedId;

          return (
            <button
              key={item.id}
              type="button"
              onClick={() => onChange(item.id)}
              style={{
                ...rowStyle,
                background: isSelected ? `${C.neon}14` : isAi ? `${C.violet}08` : 'transparent',
                borderLeft: isSelected
                  ? `3px solid ${C.neon}`
                  : isAi
                    ? `3px solid ${C.violet}`
                    : '3px solid transparent',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
                <div style={{
                  fontSize:   14,
                  fontWeight: isSelected ? 600 : 500,
                  color:      isSelected ? C.text : C.textSoft,
                  fontFamily: F.display,
                }}>
                  {item.title}
                </div>
                <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                  {isAi && <Badge color={C.violet}>AI</Badge>}
                  <Badge color={statusColor(item.status)}>{item.status}</Badge>
                </div>
              </div>
              <div style={{
                fontSize:   11,
                color:      C.dim,
                marginTop:  4,
                fontFamily: F.mono,
              }}>
                {item.projectName ?? 'no project'}
                {item.phaseName ? ` · ${item.phaseName}` : ''}
              </div>
            </button>
          );
        })}
      </div>

      {value != null && (
        <button
          type="button"
          onClick={() => onChange(null)}
          style={clearButtonStyle}
        >
          Clear selection
        </button>
      )}
    </div>
  );
}

// ─── Sub-components ────────────────────────────────────────────────────────

function Badge({ color, children }: { color: string; children: React.ReactNode }) {
  return (
    <span style={{
      fontSize:      10,
      fontWeight:    600,
      letterSpacing: '0.08em',
      textTransform: 'uppercase',
      color,
      padding:       '2px 6px',
      borderRadius:  4,
      background:    `${color}14`,
      border:        `1px solid ${color}38`,
      fontFamily:    F.display,
    }}>
      {children}
    </span>
  );
}

// ─── Styles ────────────────────────────────────────────────────────────────

const searchInputStyle: React.CSSProperties = {
  width:        '100%',
  padding:      '10px 14px',
  fontSize:     14,
  fontFamily:   F.display,
  color:        C.text,
  background:   C.surface,
  border:       `1px solid ${C.faint}`,
  borderRadius: 8,
  outline:      'none',
};

const loadingBoxStyle: React.CSSProperties = {
  padding:      '20px',
  border:       `1px solid ${C.faint}`,
  borderRadius: 8,
  background:   C.surface,
  color:        C.muted,
  fontSize:     13,
  fontFamily:   F.display,
  textAlign:    'center',
};

const rowStyle: React.CSSProperties = {
  display:      'block',
  width:        '100%',
  textAlign:    'left',
  padding:      '12px 14px',
  background:   'transparent',
  border:       'none',
  borderBottom: `1px solid ${C.faint}`,
  cursor:       'pointer',
  transition:   'background 0.12s ease',
};

const emptyRowStyle: React.CSSProperties = {
  padding:    '20px',
  textAlign:  'center',
  color:      C.dim,
  fontSize:   13,
  fontFamily: F.display,
};

const clearButtonStyle: React.CSSProperties = {
  marginTop:    8,
  padding:      '6px 12px',
  fontSize:     12,
  background:   'transparent',
  color:        C.dim,
  border:       `1px solid ${C.faint}`,
  borderRadius: 6,
  cursor:       'pointer',
  fontFamily:   F.display,
};

function statusColor(status: string): string {
  const s = status.toLowerCase();
  if (s.includes('complete') || s.includes('done')) return C.neon;
  if (s.includes('progress') || s.includes('active')) return C.cyan;
  if (s.includes('block'))                            return C.coral;
  return C.dim;
}
